/**
 * HealthyStatusCard Component
 * 
 * Card component that displays the healthy baseline sound file waveform
 * from the baseline API endpoint.
 */

import React from 'react';
import Card from '@amzn/meridian/card';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import { useUltsBaseline } from '../../../hooks';
import { WavFilePlayer } from './wav-file-player';

export interface HealthyStatusCardProps {
  /** Site identifier */
  site: string;
  /** Asset identifier */
  asset: string;
  /** Position identifier (may include equipment prefix) */
  position: string;
}

/**
 * HealthyStatusCard component that displays baseline waveform
 */
export const HealthyStatusCard: React.FC<HealthyStatusCardProps> = ({
  site,
  asset,
  position
}) => {
  const { data: baseline, isLoading, error, retry } = useUltsBaseline({
    site,
    asset,
    position
  });

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Healthy Spectrogram/Waveform Status</Text>
          <Row spacing="200">
            <Loader size="small" type="circular" />
            <Text type="b200">Loading baseline data...</Text>
          </Row>
        </Column>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Healthy Spectrogram/Waveform Status</Text>
          <Text type="b200">No healthy baseline available</Text>
        </Column>
      </Card>
    );
  }

  // No baseline data
  if (!baseline) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Healthy Spectrogram/Waveform Status</Text>
          <Text type="b200">No baseline data available</Text>
        </Column>
      </Card>
    );
  }

  // No sound URL in baseline
  if (!baseline.wavFileUrl) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Healthy Spectrogram/Waveform Status</Text>
          <Text type="b200">No healthy baseline sound file available</Text>
        </Column>
      </Card>
    );
  }

  // Display baseline sound file
  return (
    <Card>
      <Column spacing="300">
        <Text type="h100">Healthy Spectrogram/Waveform Status</Text>
        
        <Column spacing="200">
          <Row spacing="100">
            <Text type="b300">Baseline Value:</Text>
            <Text type="b200">{baseline.baseline} dB</Text>
          </Row>
        </Column>
        
        <WavFilePlayer 
          wavFileUrl={baseline.wavFileUrl}
          onError={(error) => {
            console.error('Spectrogram/Waveform WAV file player error:', error);
          }}
          onReady={() => {
            console.log('Spectrogram/Waveform WAV file player ready');
          }}
        />
      </Column>
    </Card>
  );
};
